import clsx from 'clsx';
import React from 'react';
import type { IconName } from '../../icons/types';
import Icon from '../Icon/Icon';

export type ButtonVariant =
  | 'primary'
  | 'primary-outline'
  | 'primary-ghost'
  | 'primary-soft'
  | 'secondary'
  | 'secondary-outline'
  | 'secondary-ghost'
  | 'inverse-ghost'
  | 'neutral-soft'
  | 'neutral-outline'
  | 'dark'
  | 'warning'
  | 'info'
  | 'plain'
  | 'plain-primary';

export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg';
export type ButtonShape = 'rounded' | 'pill';
export type ButtonType = 'button' | 'submit' | 'reset';

type ButtonProps = {
  ariaControls?: string;
  ariaExpanded?: boolean;
  ariaHasPopup?: 'dialog' | 'menu' | 'listbox' | 'tree' | 'grid';
  ariaLabel?: string;
  children?: React.ReactNode;
  className?: string;
  isDisabled?: boolean;
  isFullWidth?: boolean;
  isIconOnly?: boolean;
  isLoading?: boolean;
  leftIcon?: IconName;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  rightIcon?: IconName;
  shape?: ButtonShape;
  size?: ButtonSize;
  testid?: string;
  text?: string;
  type?: ButtonType;
  variant?: ButtonVariant;
};

function ButtonSpinner(): JSX.Element {
  return (
    <span aria-hidden="true" className="ui-button__spinner">
      <svg className="ui-button__spinner-svg" viewBox="0 0 24 24" focusable="false">
        <circle
          className="ui-button__spinner-track"
          cx="12"
          cy="12"
          r="9"
          fill="none"
          strokeWidth="3"
        />
        <circle
          className="ui-button__spinner-head"
          cx="12"
          cy="12"
          r="9"
          fill="none"
          strokeWidth="3"
          strokeLinecap="round"
        />
      </svg>
    </span>
  );
}

function warnIfIconOnlyMissingAccessibleName(
  isIconOnly: boolean,
  text: string | undefined,
  ariaLabel: string | undefined,
  children: React.ReactNode,
): void {
  if (process.env.NODE_ENV === 'production' || !isIconOnly) return;
  const hasVisibleText = Boolean(text) || Boolean(children);
  if (!hasVisibleText && !ariaLabel) {
    console.warn('[Button] isIconOnly=true requires an `ariaLabel` (or visible text) to be accessible.');
  }
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      ariaControls,
      ariaExpanded,
      ariaHasPopup,
      ariaLabel,
      children,
      className,
      isDisabled = false,
      isFullWidth = false,
      isIconOnly = false,
      isLoading = false,
      leftIcon,
      onClick,
      rightIcon,
      shape = 'rounded',
      size = 'md',
      testid,
      text,
      type = 'button',
      variant = 'primary',
      ...restProps
    },
    ref,
  ) => {
    warnIfIconOnlyMissingAccessibleName(isIconOnly, text, ariaLabel, children);

    const isInteractionBlocked = isDisabled || isLoading;

    const rootClassName = clsx(
      'ui-button',
      `ui-button--variant-${variant}`,
      `ui-button--size-${size}`,
      `ui-button--shape-${shape}`,
      {
        'ui-button--full-width': isFullWidth,
        'ui-button--icon-only': isIconOnly,
        'ui-button--loading': isLoading,
      },
      className,
    );

    return (
      <button
        {...restProps}
        ref={ref}
        aria-busy={isLoading || undefined}
        aria-controls={ariaControls}
        aria-disabled={isDisabled || undefined}
        aria-expanded={ariaExpanded}
        aria-haspopup={ariaHasPopup}
        aria-label={ariaLabel}
        className={rootClassName}
        data-testid={testid}
        disabled={isInteractionBlocked}
        onClick={onClick}
        type={type}
      >
        <span className="ui-button__content">
          {leftIcon && (
            <span aria-hidden="true" className="ui-button__icon ui-button__icon--left">
              <Icon color="inherit" name={leftIcon} />
            </span>
          )}
          {!isIconOnly && text && <span className="ui-button__text">{text}</span>}
          {!isIconOnly && children}
          {rightIcon && (
            <span aria-hidden="true" className="ui-button__icon ui-button__icon--right">
              <Icon color="inherit" name={rightIcon} />
            </span>
          )}
        </span>
        {isLoading && <ButtonSpinner />}
      </button>
    );
  },
);

Button.displayName = 'Button';

export default Button;
